import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Globe, Lock, Users, Film, Tv, Layers } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { effectiveTier } from '../utils/entitlements';
import { UpgradeCTA } from './ui/UpgradeCTA';
import { ApiError } from '../lib/api';

export type MediaFilter = 'movie' | 'tv' | 'mixed';

interface CreateListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (watchlist: { id: string; name: string; description?: string; visibility: string; media_filter: MediaFilter }) => void;
  createWatchlist: (name: string, description?: string, visibility?: 'public' | 'friends' | 'private', mediaFilter?: MediaFilter) => Promise<unknown>;
}

export function CreateListModal({ isOpen, onClose, onCreated, createWatchlist }: CreateListModalProps) {
  const { profile } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'friends' | 'private'>('private');
  const [mediaFilter, setMediaFilter] = useState<MediaFilter>('movie');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isFree = !!profile && effectiveTier(profile) === 'free';

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('List name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const watchlist = await createWatchlist(name.trim(), description.trim() || undefined, visibility, mediaFilter);
      if (watchlist) {
        onCreated(watchlist as { id: string; name: string; description?: string; visibility: string; media_filter: MediaFilter });
        // Reset form
        setName('');
        setDescription('');
        setVisibility('private');
        setMediaFilter('movie');
        onClose();
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'SOCIAL_TIER_REQUIRED') {
        setError('Upgrade to Social to create custom lists');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create list');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setName('');
      setDescription('');
      setVisibility('private');
      setMediaFilter('movie');
      setError(null);
      onClose();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#0A0A0A]/80 backdrop-blur-sm"
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
      />

      {/* Modal */}
      <div
        className="relative bg-[#0F1419] rounded-xl ring-1 ring-white/[0.06] w-full max-w-md shadow-2xl shadow-black/50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <h2 className="text-lg font-semibold text-white">Create New List</h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 text-slate-400 hover:text-white hover:bg-[#1A1F26] rounded-lg transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Upgrade CTA for Free users */}
        {isFree ? (
          <div className="p-4">
            <UpgradeCTA feature="Creating custom lists" onNavigate={handleClose} />
            <div className="mt-4">
              <button
                type="button"
                onClick={handleClose}
                className="w-full px-4 py-2 text-slate-300 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg hover:bg-slate-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
        /* Form */
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Name */}
          <div>
            <label htmlFor="list-name" className="block text-sm font-medium text-slate-300 mb-1">
              List Name *
            </label>
            <input
              id="list-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Awesome List"
              maxLength={100}
              disabled={loading}
              className="w-full px-3 py-2 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="list-description" className="block text-sm font-medium text-slate-300 mb-1">
              Description
            </label>
            <textarea
              id="list-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this list about?"
              maxLength={500}
              rows={3}
              disabled={loading}
              className="w-full px-3 py-2 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50 resize-none"
            />
          </div>

          {/* Media Type */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              What's in this list?
            </label>
            <div className="space-y-2">
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                mediaFilter === 'movie'
                  ? 'border-teal-500 bg-teal-500/10'
                  : 'border-white/[0.08] hover:border-white/[0.12]'
              }`}>
                <input
                  type="radio"
                  name="mediaFilter"
                  value="movie"
                  checked={mediaFilter === 'movie'}
                  onChange={() => setMediaFilter('movie')}
                  disabled={loading}
                  className="sr-only"
                />
                <Film size={18} className={mediaFilter === 'movie' ? 'text-teal-400' : 'text-slate-500'} />
                <div>
                  <p className={`font-medium ${mediaFilter === 'movie' ? 'text-white' : 'text-slate-300'}`}>Movies only</p>
                </div>
              </label>

              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                mediaFilter === 'tv'
                  ? 'border-teal-500 bg-teal-500/10'
                  : 'border-white/[0.08] hover:border-white/[0.12]'
              }`}>
                <input
                  type="radio"
                  name="mediaFilter"
                  value="tv"
                  checked={mediaFilter === 'tv'}
                  onChange={() => setMediaFilter('tv')}
                  disabled={loading}
                  className="sr-only"
                />
                <Tv size={18} className={mediaFilter === 'tv' ? 'text-teal-400' : 'text-slate-500'} />
                <div>
                  <p className={`font-medium ${mediaFilter === 'tv' ? 'text-white' : 'text-slate-300'}`}>TV Shows only</p>
                </div>
              </label>

              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                mediaFilter === 'mixed'
                  ? 'border-teal-500 bg-teal-500/10'
                  : 'border-white/[0.08] hover:border-white/[0.12]'
              }`}>
                <input
                  type="radio"
                  name="mediaFilter"
                  value="mixed"
                  checked={mediaFilter === 'mixed'}
                  onChange={() => setMediaFilter('mixed')}
                  disabled={loading}
                  className="sr-only"
                />
                <Layers size={18} className={mediaFilter === 'mixed' ? 'text-teal-400' : 'text-slate-500'} />
                <div>
                  <p className={`font-medium ${mediaFilter === 'mixed' ? 'text-white' : 'text-slate-300'}`}>Movies & TV Shows</p>
                </div>
              </label>
            </div>
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Visibility
            </label>
            <div className="space-y-2">
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                visibility === 'private'
                  ? 'border-teal-500 bg-teal-500/10'
                  : 'border-white/[0.08] hover:border-white/[0.12]'
              }`}>
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={visibility === 'private'}
                  onChange={() => setVisibility('private')}
                  disabled={loading}
                  className="sr-only"
                />
                <Lock size={18} className={visibility === 'private' ? 'text-teal-400' : 'text-slate-500'} />
                <div>
                  <p className={`font-medium ${visibility === 'private' ? 'text-white' : 'text-slate-300'}`}>Private</p>
                  <p className="text-sm text-slate-500">Only you can see this list</p>
                </div>
              </label>

              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                visibility === 'friends'
                  ? 'border-teal-500 bg-teal-500/10'
                  : 'border-white/[0.08] hover:border-white/[0.12]'
              }`}>
                <input
                  type="radio"
                  name="visibility"
                  value="friends"
                  checked={visibility === 'friends'}
                  onChange={() => setVisibility('friends')}
                  disabled={loading}
                  className="sr-only"
                />
                <Users size={18} className={visibility === 'friends' ? 'text-teal-400' : 'text-slate-500'} />
                <div>
                  <p className={`font-medium ${visibility === 'friends' ? 'text-white' : 'text-slate-300'}`}>Friends</p>
                  <p className="text-sm text-slate-500">Only your followers can see this</p>
                </div>
              </label>

              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                visibility === 'public'
                  ? 'border-teal-500 bg-teal-500/10'
                  : 'border-white/[0.08] hover:border-white/[0.12]'
              }`}>
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={visibility === 'public'}
                  onChange={() => setVisibility('public')}
                  disabled={loading}
                  className="sr-only"
                />
                <Globe size={18} className={visibility === 'public' ? 'text-teal-400' : 'text-slate-500'} />
                <div>
                  <p className={`font-medium ${visibility === 'public' ? 'text-white' : 'text-slate-300'}`}>Public</p>
                  <p className="text-sm text-slate-500">Anyone can see and follow this list</p>
                </div>
              </label>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-2 text-slate-300 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Creating...
                </>
              ) : (
                'Create List'
              )}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>,
    document.body
  );
}
